<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h1>🔧 Warranty Performance Hub</h1>
      <div class="subhead">
        Technician × Status aging breakdown | ≤10d · 11–20d · ≥21d | Full transparency
      </div>
    </div>

    <!-- 调试信息：如果看到这行说明组件已加载 -->
    <div style="background:#e6f7e6; padding:8px 16px; margin:0 32px; border-radius:8px; text-align:center;">
      ✅ Vue Component Loaded Successfully | Status: {{ loadingStatus }}
    </div>

    <div class="charts-row">
      <div class="chart-card">
        <div class="chart-header">📊 Global distribution by warranty status</div>
        <div class="chart-box" ref="statusChartRef" style="min-height:300px;">
          <div v-if="loading.globalStatus" class="loader-overlay">
            <div class="skeleton-chart"></div>
            <div class="progress-bar-container">
              <div class="progress-fill" :style="{ width: progress.globalStatus + '%' }"></div>
            </div>
            <div class="loading-text">{{ loadingText }}</div>
          </div>
        </div>
      </div>
      <div class="chart-card">
        <div class="chart-header">👥 Total workload by technician</div>
        <div class="chart-box" ref="techChartRef" style="min-height:300px;">
          <div v-if="loading.globalTech" class="loader-overlay">
            <div class="skeleton-chart"></div>
            <div class="progress-bar-container">
              <div class="progress-fill" :style="{ width: progress.globalTech + '%' }"></div>
            </div>
            <div class="loading-text">{{ loadingText }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-section">
      <div class="section-label">
        <h3>📋 Master view · Technician & status breakdown</h3>
        <div class="badge-time">⏱️ Aging buckets: ≤10d / 11–20d / ≥21d</div>
      </div>
      <div v-if="errorMessage" class="error-message">
        ⚠️ {{ errorMessage }}
      </div>
      <div class="table-wrapper" v-else-if="!loading.masterTable && masterRows.length > 0">
        <table class="stats-table">
          <thead>
            <tr><th>TECHNICIAN</th><th>TOTAL</th><th>STATUS</th><th>COUNT</th><th>≤10d</th><th>RATE</th><th>11-20d</th><th>RATE</th><th>≥21d</th><th>RATE</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in masterRows.slice(0, 50)" :key="idx">
              <td class="technician-name">{{ row.Technician }}</td>
              <td><strong>{{ row.TotalCount }}</strong></td>
              <td><span class="status-pill">{{ row.warrantystatus }}</span></td>
              <td>{{ row.statuswarrantycount }}</td>
              <td>{{ row.Within10Days }}</td>
              <td class="percent-cell">{{ row.Rate10Days }}</td>
              <td>{{ row.Within11To20Days }}</td>
              <td class="percent-cell">{{ row.Rate11To20Days }}</td>
              <td>{{ row.Beyond21Days }}</td>
              <td class="percent-cell">{{ row.RateBeyond21Days }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="loading.masterTable" class="loading-placeholder">
        <div class="progress-bar-container" style="width:200px; margin:0 auto;">
          <div class="progress-fill" :style="{ width: progress.masterTable + '%' }"></div>
        </div>
        <div class="loading-text">Fetching warranty data...</div>
      </div>
    </div>

    <div class="sub-pies-section">
      <div class="sub-title">
        <h2>📌 Technician contribution per status</h2>
      </div>
      <div v-if="!loading.subPies && statusCards.length > 0">
        <div v-for="card in statusCards" :key="card.status" class="status-card">
          <div class="status-card-header">
            <span>🏷️ {{ card.status }}</span>
            <span class="status-total-badge">Total: {{ card.totalStatusTickets }}</span>
          </div>
          <div class="pie-and-stats">
            <div class="pie-col">
              <div :id="'pie-' + card.statusKey" class="pie-small"></div>
            </div>
            <div class="stats-col">
              <table class="sub-status-table">
                <thead><tr><th>Tech</th><th>Total</th><th>≤10d</th><th>%</th><th>11-20d</th><th>%</th><th>≥21d</th><th>%</th></tr></thead>
                <tbody>
                  <tr v-for="tech in card.techRows" :key="tech.technician">
                    <td>{{ tech.technician }}</td>
                    <td>{{ tech.totalInStatus }}</td>
                    <td>{{ tech.within10 }}</td><td>{{ tech.pct10 }}</td>
                    <td>{{ tech.within1120 }}</td><td>{{ tech.pct1120 }}</td>
                    <td>{{ tech.beyond21 }}</td><td>{{ tech.pctBeyond }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="loading.subPies" class="loading-placeholder">
        <div class="loading-text">Preparing status breakdown...</div>
      </div>
    </div>
    <footer>Data source: SmartWarrior API | Powered by Vue 2 + ECharts</footer>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'WarrantyPerformanceHub',
  data() {
    return {
      loadingStatus: 'initializing',
      loadingText: 'Loading dashboard...',
      errorMessage: '',
      loading: {
        globalStatus: true,
        globalTech: true,
        masterTable: true,
        subPies: true,
      },
      progress: {
        globalStatus: 0,
        globalTech: 0,
        masterTable: 0,
        subPies: 0,
      },
      masterRows: [],
      statusCards: [],
      rawData: [],
      globalStatusChart: null,
      globalTechChart: null,
      statusCharts: [],
      progressInterval: null,
    };
  },
  mounted() {
    console.log('Component mounted successfully');
    this.loadingStatus = 'mounted';
    this.startProgressAnimations();
    this.fetchData();
  },
  beforeDestroy() {
    if (this.progressInterval) clearInterval(this.progressInterval);
    if (this.globalStatusChart) this.globalStatusChart.dispose();
    if (this.globalTechChart) this.globalTechChart.dispose();
    this.statusCharts.forEach(chart => chart && chart.dispose());
  },
  methods: {
    startProgressAnimations() {
      let p = 0;
      this.progressInterval = setInterval(() => {
        p += Math.random() * 12;
        if (p >= 90) p = 90;
        this.progress.globalStatus = p;
        this.progress.globalTech = p;
        this.progress.masterTable = p;
        this.progress.subPies = p;
      }, 200);
    },
    daysDiffFromNow(dateStr) {
      if (!dateStr) return 0;
      const target = new Date(dateStr);
      if (isNaN(target.getTime())) return 0;
      const today = new Date();
      const diffTime = today - target;
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    },
    processFullStatistics(rawData) {
      const techTotalMap = new Map();
      const detailMap = new Map();
      const statusDetailedMap = new Map();

      for (const record of rawData) {
        const tech = record.Technician || "Unknown";
        const status = record.warrantystatus || "Unknown";
        const days = this.daysDiffFromNow(record.StatusTime);
        
        techTotalMap.set(tech, (techTotalMap.get(tech) || 0) + 1);
        
        const key = `${tech}|${status}`;
        if (!detailMap.has(key)) {
          detailMap.set(key, { within10: 0, within1120: 0, beyond21: 0, statusCount: 0 });
        }
        const stat = detailMap.get(key);
        stat.statusCount += 1;
        if (days <= 10) stat.within10 += 1;
        else if (days >= 11 && days <= 20) stat.within1120 += 1;
        else if (days >= 21) stat.beyond21 += 1;
        
        if (!statusDetailedMap.has(status)) statusDetailedMap.set(status, new Map());
        const techMap = statusDetailedMap.get(status);
        if (!techMap.has(tech)) {
          techMap.set(tech, { within10: 0, within1120: 0, beyond21: 0, totalTechInStatus: 0 });
        }
        const techStats = techMap.get(tech);
        techStats.totalTechInStatus += 1;
        if (days <= 10) techStats.within10 += 1;
        else if (days >= 11 && days <= 20) techStats.within1120 += 1;
        else if (days >= 21) techStats.beyond21 += 1;
      }
      
      const tableRows = [];
      for (const [key, counts] of detailMap.entries()) {
        const [technician, statusName] = key.split('|');
        const totalForTech = techTotalMap.get(technician) || 0;
        const rate10 = totalForTech === 0 ? 0 : (counts.within10 / totalForTech) * 100;
        const rate1120 = totalForTech === 0 ? 0 : (counts.within1120 / totalForTech) * 100;
        const rateBeyond = totalForTech === 0 ? 0 : (counts.beyond21 / totalForTech) * 100;
        tableRows.push({
          Technician: technician,
          TotalCount: totalForTech,
          warrantystatus: statusName,
          statuswarrantycount: counts.statusCount,
          Within10Days: counts.within10,
          Rate10Days: rate10.toFixed(2) + '%',
          Within11To20Days: counts.within1120,
          Rate11To20Days: rate1120.toFixed(2) + '%',
          Beyond21Days: counts.beyond21,
          RateBeyond21Days: rateBeyond.toFixed(2) + '%'
        });
      }
      tableRows.sort((a,b) => {
        if (a.Technician === b.Technician) return a.warrantystatus.localeCompare(b.warrantystatus);
        return a.Technician.localeCompare(b.Technician);
      });
      
      const cards = [];
      for (let [status, techMap] of statusDetailedMap.entries()) {
        let totalStatusTickets = 0;
        const pieData = [];
        const techRows = [];
        for (let [techName, stats] of techMap.entries()) {
          const techTotalInStatus = stats.totalTechInStatus;
          totalStatusTickets += techTotalInStatus;
          pieData.push({ name: techName, value: techTotalInStatus });
          const pct10 = techTotalInStatus === 0 ? 0 : (stats.within10 / techTotalInStatus) * 100;
          const pct1120 = techTotalInStatus === 0 ? 0 : (stats.within1120 / techTotalInStatus) * 100;
          const pctBeyond = techTotalInStatus === 0 ? 0 : (stats.beyond21 / techTotalInStatus) * 100;
          techRows.push({
            technician: techName,
            totalInStatus: techTotalInStatus,
            within10: stats.within10,
            pct10: pct10.toFixed(2) + '%',
            within1120: stats.within1120,
            pct1120: pct1120.toFixed(2) + '%',
            beyond21: stats.beyond21,
            pctBeyond: pctBeyond.toFixed(2) + '%'
          });
        }
        techRows.sort((a,b) => a.technician.localeCompare(b.technician));
        pieData.sort((a,b) => b.value - a.value);
        cards.push({
          status,
          statusKey: status.replace(/[^a-zA-Z0-9]/g, '_'),
          totalStatusTickets,
          pieData,
          techRows
        });
      }
      cards.sort((a,b) => a.status.localeCompare(b.status));
      return { tableRows, techTotalMap, cards };
    },
    async fetchData() {
      this.loadingStatus = 'fetching';
      try {
        const response = await fetch('https://www.smartwarrior.com.au/api/MACPhase/api/ClientSignup/warrantydatastatusfengxi', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (json && json.code === 200 && Array.isArray(json.data)) {
          this.rawData = json.data;
          const { tableRows, techTotalMap, cards } = this.processFullStatistics(this.rawData);
          this.masterRows = tableRows;
          this.statusCards = cards;
          
          this.progress.globalStatus = 100;
          this.progress.globalTech = 100;
          this.progress.masterTable = 100;
          this.progress.subPies = 100;
          
          this.$nextTick(() => {
            this.renderGlobalCharts(techTotalMap);
            this.renderStatusCharts();
            this.loading.globalStatus = false;
            this.loading.globalTech = false;
            this.loading.masterTable = false;
            this.loading.subPies = false;
            this.loadingStatus = 'complete';
          });
        } else {
          throw new Error("Invalid API response");
        }
      } catch (err) {
        console.error('Fetch error:', err);
        this.errorMessage = err.message || 'Failed to load data';
        this.loadingStatus = 'error';
        this.loading.globalStatus = false;
        this.loading.globalTech = false;
        this.loading.masterTable = false;
        this.loading.subPies = false;
      } finally {
        if (this.progressInterval) clearInterval(this.progressInterval);
      }
    },
    renderGlobalCharts(techTotalMap) {
      const statusMap = new Map();
      for (const r of this.rawData) {
        const st = r.warrantystatus || "Unknown";
        statusMap.set(st, (statusMap.get(st) || 0) + 1);
      }
      const statusPieData = Array.from(statusMap.entries()).map(([n,v]) => ({ name: n, value: v }));
      
      const techPieData = Array.from(techTotalMap.entries()).map(([name, value]) => ({ name, value }));
      techPieData.sort((a,b) => b.value - a.value);
      
      if (this.$refs.statusChartRef) {
        if (this.globalStatusChart) this.globalStatusChart.dispose();
        this.globalStatusChart = echarts.init(this.$refs.statusChartRef);
        this.globalStatusChart.setOption({
          tooltip: { trigger: 'item', formatter: '{b}: {d}% ({c} tickets)' },
          legend: { orient: 'vertical', left: 'left' },
          series: [{ type: 'pie', radius: '55%', data: statusPieData, label: { show: true } }]
        });
      }
      
      if (this.$refs.techChartRef) {
        if (this.globalTechChart) this.globalTechChart.dispose();
        this.globalTechChart = echarts.init(this.$refs.techChartRef);
        this.globalTechChart.setOption({
          tooltip: { trigger: 'item', formatter: '{b}: {d}% ({c} jobs)' },
          legend: { orient: 'vertical', left: 'left', type: 'scroll' },
          series: [{ type: 'pie', radius: '55%', data: techPieData, label: { show: true } }]
        });
      }
    },
    renderStatusCharts() {
      this.statusCharts.forEach(chart => chart && chart.dispose());
      this.statusCharts = [];
      
      this.statusCards.forEach(card => {
        const dom = document.getElementById(`pie-${card.statusKey}`);
        if (dom) {
          const chart = echarts.init(dom);
          chart.setOption({
            tooltip: { trigger: 'item', formatter: '{b}: {d}% ({c} tickets)' },
            legend: { orient: 'vertical', left: 'left', type: 'scroll' },
            series: [{ type: 'pie', radius: '55%', data: card.pieData, label: { show: true } }]
          });
          this.statusCharts.push(chart);
        }
      });
    }
  }
};
</script>

<style>
/* 不使用 scoped，避免样式隔离问题 */
.dashboard {
  max-width: 1600px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 36px;
  box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  font-family: system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.dashboard-header { padding: 28px 32px 16px 32px; border-bottom: 1px solid #eef2f9; }
.dashboard-header h1 { font-size: 1.9rem; font-weight: 700; margin-bottom: 8px; color: #1E2F5E; }
.subhead { color: #5b6e8c; font-size: 0.9rem; border-left: 3px solid #3b82f6; padding-left: 14px; }
.charts-row { display: flex; flex-wrap: wrap; gap: 24px; padding: 24px 32px 8px 32px; }
.chart-card { flex: 1; min-width: 280px; background: #fff; border-radius: 28px; border: 1px solid #f0f2f8; }
.chart-header { padding: 18px 20px 6px 20px; font-weight: 600; font-size: 1.1rem; color: #1f2b48; border-bottom: 1px solid #f0f4fa; }
.chart-box { width: 100%; height: 360px; padding: 8px 12px 12px 12px; position: relative; }
.loader-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; }
.progress-bar-container { width: 80%; height: 6px; background: #e2e8f0; border-radius: 12px; overflow: hidden; margin-top: 16px; }
.progress-fill { height: 100%; background: #3b82f6; border-radius: 12px; transition: width 0.25s ease-out; }
.loading-text { font-size: 0.8rem; color: #5b6e8c; margin-top: 12px; }
.skeleton-chart { width: 90%; height: 70%; margin: 0 auto; background: linear-gradient(90deg, #f0f2f5 25%, #e9edf2 50%, #f0f2f5 75%); background-size: 200% 100%; animation: shimmer 1.2s infinite; border-radius: 20px; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
.table-section { padding: 8px 32px 24px 32px; }
.section-label { display: flex; justify-content: space-between; flex-wrap: wrap; margin-bottom: 18px; }
.badge-time { background: #ecfdf5; color: #0b5e42; font-size: 0.7rem; padding: 5px 12px; border-radius: 40px; border: 1px solid #c0f0df; }
.table-wrapper { border-radius: 24px; border: 1px solid #edf2f7; overflow-x: auto; }
.stats-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; min-width: 1100px; }
.stats-table th { background: #fafcff; padding: 16px 12px; border-bottom: 1.5px solid #e6edf4; text-align: center; }
.stats-table td { padding: 14px 10px; text-align: center; border-bottom: 1px solid #f1f5f9; }
.stats-table tr:hover td { background-color: #fefce8; }
.technician-name { font-weight: 700; color: #1e4a76; text-align: left; padding-left: 20px; }
.status-pill { background: #eef2ff; padding: 5px 12px; border-radius: 30px; font-size: 0.75rem; font-weight: 600; color: #1e3a8a; display: inline-block; }
.percent-cell { font-weight: 600; color: #2c6e2f; }
.sub-pies-section { margin-top: 16px; padding: 16px 32px 40px 32px; border-top: 1px solid #eef3fc; }
.sub-title { margin-bottom: 28px; }
.sub-title h2 { font-size: 1.45rem; font-weight: 700; color: #1e3b5c; }
.status-card { background: #fff; border-radius: 28px; border: 1px solid #eef3fa; margin-bottom: 36px; }
.status-card-header { background: #fafdff; padding: 18px 24px; font-weight: 700; font-size: 1.2rem; border-bottom: 1px solid #ecf3f9; border-radius: 28px 28px 0 0; color: #155f8c; display: flex; justify-content: space-between; }
.status-total-badge { font-size: 0.8rem; background: #eef2ff; padding: 5px 14px; border-radius: 40px; }
.pie-and-stats { display: flex; flex-wrap: wrap; gap: 16px; padding: 16px 20px 20px 20px; }
.pie-col { flex: 1.2; min-width: 260px; min-height: 280px; }
.stats-col { flex: 2; min-width: 320px; overflow-x: auto; }
.pie-small { width: 100%; height: 280px; }
.sub-status-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
.sub-status-table th { background: #f8fafc; padding: 10px 8px; text-align: center; }
.sub-status-table td { padding: 8px 6px; text-align: center; border-bottom: 1px solid #f0f4fa; }
.micro-percent { font-weight: 600; color: #256f4a; }
.error-message { background: #fee2e2; color: #b91c1c; padding: 16px; border-radius: 16px; margin-bottom: 20px; text-align: center; }
.loading-placeholder { text-align: center; padding: 60px; background: #fafcff; border-radius: 24px; }
footer { font-size: 0.7rem; text-align: center; padding: 20px 32px 28px; color: #6f7c91; border-top: 1px solid #ecf2f9; }
@media (max-width: 780px) { .pie-and-stats { flex-direction: column; } }
</style>